import {
  activeCodAgeConditions,
  COD_AGE_OPERATORS,
  type CodAgeFilterValue,
} from "@/dashboard/inventory_management/common/portfolioTableColumns";
import {
  FILTER_COLUMN_KEYS,
  FILTER_COLUMNS,
  filterValuesEqual,
  isFilterValueActive,
  matchFilterKind,
  type FilterColumn,
  type FilterOption,
  type FilterScalar,
  type FilterState,
  type FilterValue,
} from "@/dashboard/inventory_management/common/portfolioTableFilters";
import { isEmpty } from "lodash-es";

// The table's active filters: which columns filter and why (classification), and the chips that
// show them (formatting). Pages read the classification's summary; only the chip row reads text.

export type FilterChipTag = "default" | "document" | "unavailable";

/**
 * Filters a document (the Cancel AI flow) applied to the table. A column's chip keeps a "From
 * document" tag while the column still holds the document's value; `unavailable` columns, whose
 * requested values match no inventory, render as danger chips.
 */
export interface DocumentFilterState {
  filters: Partial<FilterState>;
  unavailable: readonly FilterColumn[];
}

/** An active column filter, tagged with what set it (default, document) while it still holds that value. */
export interface ActiveFilter {
  column: FilterColumn;
  value: FilterValue;
  tag: FilterChipTag | null;
}

export interface FilterSummary {
  hasActiveFilters: boolean;
  /** Columns still holding what a document applied (tagged "From document" or "No matching inventory"). */
  documentColumns: FilterColumn[];
  /** Document columns whose requested values match no inventory. */
  unavailableColumns: FilterColumn[];
}

export interface FilterChip {
  column: FilterColumn;
  text: string;
  tag: FilterChipTag | null;
  removeLabel: string;
}

type Translate = (key: string, named?: Record<string, unknown>) => string;

/**
 * Every active filter in the table's column order, including filters on columns hidden by the
 * column selector. A column holding the document's value is tagged for the document even when that
 * value is also the column's default.
 */
export const classifyActiveFilters = ({
  values,
  defaults,
  document,
}: {
  values: FilterState;
  defaults: Partial<FilterState>;
  document: DocumentFilterState | null;
}): ActiveFilter[] =>
  FILTER_COLUMN_KEYS.filter((column) =>
    isFilterValueActive(column, values[column])
  ).map((column) => ({
    column,
    value: values[column],
    tag: filterTag(column, values[column], defaults, document),
  }));

/** What pages act on, read off the classification alone: no labels or options are involved. */
export const summarizeActiveFilters = (
  filters: readonly ActiveFilter[]
): FilterSummary => ({
  hasActiveFilters: filters.length > 0,
  documentColumns: filters
    .filter(({ tag }) => tag === "document" || tag === "unavailable")
    .map(({ column }) => column),
  unavailableColumns: filters
    .filter(({ tag }) => tag === "unavailable")
    .map(({ column }) => column),
});

/** The summary of a table with no active filters. */
export const EMPTY_FILTER_SUMMARY: FilterSummary = summarizeActiveFilters([]);

/**
 * The chip for one active filter. An unavailable document filter names its values as requested,
 * since they need not exist among the column's options.
 */
export const formatFilterChip = (
  { column, value, tag }: ActiveFilter,
  {
    options,
    t,
  }: { options: Partial<Record<FilterColumn, FilterOption[]>>; t: Translate }
): FilterChip => {
  const label = t(FILTER_COLUMNS[column].header);
  const text =
    tag === "unavailable"
      ? formatRequestedValues(value)
      : formatFilterChipValue({
          column,
          value,
          options: options[column] ?? [],
          t,
        });
  return {
    column,
    text: t("dashboard.portfolio.table.filter_chips.value", {
      column: label,
      value: text,
    }),
    tag,
    removeLabel: t("dashboard.portfolio.table.filter_chips.remove", {
      column: label,
    }),
  };
};

/** Which label the chip row's trailing reset link carries. */
export type TrailingFilterAction = "reset" | "clear";

/**
 * The trailing link of the chip row, which puts every column back to its default: "Reset to default
 * filters" once any column differs from its default (including a defaulted column the user cleared),
 * "Clear all" on pages without defaults, where the defaults are all empty.
 */
export const trailingFilterAction = (
  values: FilterState,
  defaults: Partial<FilterState>
): TrailingFilterAction | null => {
  const changed = FILTER_COLUMN_KEYS.some(
    (column) => !filterValuesEqual(column, values[column], defaults[column])
  );
  if (!changed) return null;
  return isEmpty(defaults) ? "clear" : "reset";
};

/**
 * The value part of a chip. List filters name what they show while that is short, else what they
 * hide, else how many of the column's options they show. Values are named by their option labels.
 */
export const formatFilterChipValue = <C extends FilterColumn>({
  column,
  value,
  options,
  t,
}: {
  column: C;
  value: FilterValue<C>;
  options: FilterOption[];
  t: Translate;
}): string =>
  matchFilterKind(column, value, {
    codAge: (filter) =>
      t("dashboard.portfolio.table.filter_chips.cod_age_years", {
        conditions: formatCodAgeConditions(filter),
      }),
    text: (text) =>
      text === null
        ? ""
        : t("dashboard.portfolio.table.filter_chips.contains", {
            text: text.trim(),
          }),
    list: (values) =>
      formatListChipValue({ column, value: values, options, t }),
  });

const filterTag = <C extends FilterColumn>(
  column: C,
  value: FilterValue<C>,
  defaults: Partial<FilterState>,
  document: DocumentFilterState | null
): FilterChipTag | null => {
  if (document) {
    const documentValue = document.filters[column];
    if (
      documentValue !== undefined &&
      filterValuesEqual(column, value, documentValue)
    ) {
      return document.unavailable.includes(column) ? "unavailable" : "document";
    }
  }
  if (
    column in defaults &&
    filterValuesEqual(column, value, defaults[column])
  ) {
    return "default";
  }
  return null;
};

// A raw join, not the list rules: a requested month range would otherwise read "hiding …" or
// "N of M" against options it has no stock in. Only list criteria can match no inventory.
const formatRequestedValues = (value: FilterValue): string =>
  Array.isArray(value) ? value.map(String).join(", ") : "";

const formatCodAgeConditions = (filter: CodAgeFilterValue): string =>
  activeCodAgeConditions(filter)
    .map((condition) => `${COD_AGE_OPERATORS[condition.op]} ${condition.value}`)
    .join(" & ");

const formatListChipValue = ({
  column,
  value,
  options,
  t,
}: {
  column: FilterColumn;
  value: FilterScalar[];
  options: FilterOption[];
  t: Translate;
}): string => {
  const labelOf = (item: FilterScalar) =>
    options.find((option) => String(option.value) === String(item))?.label ??
    String(item);

  // Support has only Yes / No / Unknown, so its chip always names them rather than counting.
  if (column === "production_support") return value.map(labelOf).join(", ");

  if (column === "production_month" && isMonthRange(value, options)) {
    const months = value.map(String).sort();
    return `${months[0]} – ${months[months.length - 1]}`;
  }

  const included = new Set(value.map(String));
  const hidden = options.filter(
    (option) => !included.has(String(option.value))
  );
  if (value.length <= 2) return value.map(labelOf).join(", ");
  if (hidden.length > 0 && hidden.length <= 2) {
    return t("dashboard.portfolio.table.filter_chips.hiding", {
      values: hidden.map((option) => option.label).join(", "),
    });
  }
  return t("dashboard.portfolio.table.filter_chips.count_of_total", {
    shown: value.length,
    total: options.length,
  });
};

// More than one month reads as a range when it covers every month in stock between its ends; a
// selection that skips a stocked month in between is listed instead, so the chip never overstates.
const isMonthRange = (
  value: FilterScalar[],
  options: FilterOption[]
): boolean => {
  if (value.length < 2) return false;
  const months = value.map(String).sort();
  const first = months[0];
  const last = months[months.length - 1];
  const selected = new Set(months);
  return options.every((option) => {
    const month = String(option.value);
    return month < first || month > last || selected.has(month);
  });
};
